"use client"

import { graphql } from "@/gql"
import { useCallback, useMemo, useState } from "react"
import { useRolesFilter } from "./hooks/useRolesFilter"
import { FilterItem } from "./FilterItem"
import { FilterWrapper } from "./FilterWrapper"
import { useFilters } from "./useFilters"

graphql(`
  query RolesFilters($first: Int, $after: String, $orderBy: String) {
    allCreativeProfessions(first: $first, after: $after, orderBy: $orderBy) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        ...RolesFilterItem
      }
    }
  }
`)

graphql(`
  fragment RolesFilterItem on ProfessionCategoryEdge {
    node {
      name
      id
      pluralizedName
    }
  }
`)

interface Props {
  pluralizedName?: boolean
  onSelect?: (filterName: string) => void
}

const id = "roles"

export const RolesFilter: React.FC<Props> = ({ pluralizedName = false, onSelect }) => {
  const [search, setSearch] = useState<string>("")
  const { toggleFilter, getActiveFilters, removeAllByIds, getButtonText } = useFilters()

  const roles = useRolesFilter()

  const rolesSearched = useMemo(
    () => roles.filter(edge => edge?.node?.name.toLowerCase().includes(search.toLowerCase())),
    [roles, search]
  )

  const selectedIds = getActiveFilters(id)

  const onSelectItem = (filterId: string, filterName: string) => {
    toggleFilter({ params: [{ key: id, values: [filterId] }] })
    onSelect?.(filterName)
  }

  const buttonTextData = useMemo(() => {
    return roles?.map(edge => {
      return {
        name: pluralizedName ? edge?.node?.pluralizedName! : edge?.node.name!,
        id: edge?.node?.id!,
      }
    })
  }, [roles, pluralizedName])

  const buttonText = getButtonText({
    defaultText: "Roles",
    data: buttonTextData ?? [],
    selectedIds,
  })

  const onRemoveAll = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.stopPropagation()
      removeAllByIds([id])
    },
    [removeAllByIds]
  )

  return (
    <FilterWrapper
      id={id}
      onRemoveAll={onRemoveAll}
      filterActive={selectedIds?.length > 0}
      buttonText={buttonText}
      search={search}
      onChangeSearch={e => setSearch(e.target.value)}>
      <div className="mt-3 flex w-full">
        <div className="flex w-full flex-col gap-1">
          {rolesSearched?.map(edge => {
            const node = edge?.node
            if (!node) return null
            return (
              <FilterItem
                key={node.id}
                name={pluralizedName ? node.pluralizedName : node.name}
                selected={selectedIds.includes(node.id)}
                onClick={() => onSelectItem(node.id, node.name)}
              />
            )
          })}
        </div>
      </div>
    </FilterWrapper>
  )
}
